import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Award, 
  Download,
  Share2,
  Loader2,
  Eye,
  X,
  Maximize2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fullscreen Certificate Modal Component
const CertificateModal = ({ certificate, qrData, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-auto">
      <button 
        onClick={onClose}
        className="fixed top-4 right-4 text-white/80 hover:text-white z-50 bg-black/50 rounded-full p-2"
        data-testid="close-certificate-modal"
      >
        <X className="h-6 w-6" />
      </button>
      
      <div className="min-h-full flex flex-col items-center justify-start p-4 pt-16 pb-8">
        <div className="text-center text-white/80 text-xs mb-4">
          Pinch to zoom • Rotate for best view
        </div>
        
        <div 
          className="w-full max-w-2xl touch-manipulation"
          style={{ touchAction: 'pinch-zoom pan-x pan-y' }}
        >
          <div 
            className="relative overflow-hidden rounded-lg shadow-2xl bg-[#0A1628] w-full"
            style={{ aspectRatio: '1.414/1' }}
          >
            <img 
              src="/certificate-bg.png" 
              alt="Certificate Background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            <div className="absolute inset-0 flex flex-col items-center text-center px-[8%] pt-[26%]">
              <h3 className="text-[#C9A227] text-[3.5vw] sm:text-lg md:text-xl font-bold tracking-wide mb-1">
                {certificate.member_name}
              </h3>
              
              <p className="text-white text-[2vw] sm:text-[10px] md:text-xs max-w-[90%] leading-relaxed">
                has been admitted as a <span className="text-[#C9A227] font-semibold uppercase">{certificate.membership_type}</span> MEMBER of UIEHEC,
                in recognition of their commitment to international cooperation,
                academic excellence, and the advancement of higher education.
              </p>
              
              <p className="text-white/70 text-[1.8vw] sm:text-[9px] md:text-[10px] mt-1 max-w-[85%]">
                Membership is effective from {certificate.valid_from || certificate.issue_date} and remains valid until {certificate.valid_until || 'Indefinite'},
                subject to the UIEHEC Charter and internal regulations.
              </p>
              
              <div className="border border-[#C9A227]/50 rounded flex divide-x divide-[#C9A227]/30 mt-2 max-w-[85%] w-full">
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Certificate No:</p>
                  <p className="text-white font-mono text-[1.8vw] sm:text-[9px]">{certificate.certificate_number}</p>
                </div>
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Type:</p>
                  <p className="text-white uppercase text-[1.8vw] sm:text-[9px]">{certificate.membership_type}</p>
                </div>
                <div className="flex-1 p-1 text-center">
                  <p className="text-[#C9A227] font-semibold text-[1.5vw] sm:text-[8px]">Issue Date:</p>
                  <p className="text-white text-[1.8vw] sm:text-[9px]">{certificate.issue_date}</p>
                </div>
              </div>
            </div>

            {qrData?.verification_url && (
              <div className="absolute bottom-[8%] right-[5%] bg-white p-0.5 rounded shadow-lg">
                <QRCodeSVG 
                  value={qrData.verification_url}
                  size={25}
                  level="M"
                />
              </div>
            )}

            <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
              certificate.status === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              {certificate.status?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Certificate Details Below */}
        <div className="w-full max-w-2xl mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Certificate</p>
            <p className="text-white text-xs font-mono">{certificate.certificate_number}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Type</p>
            <p className="text-white text-xs uppercase">{certificate.membership_type}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <p className="text-white/60 text-[10px]">Status</p>
            <p className="text-green-400 text-xs uppercase">{certificate.status}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MemberCertificate() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const [certResponse, qrResponse] = await Promise.all([
          axios.get(`${API}/certificates/my`, getAuthHeader()),
          axios.get(`${API}/certificates/my/qr`, {
            ...getAuthHeader(),
            headers: {
              ...getAuthHeader().headers,
              'Origin': window.location.origin
            }
          })
        ]);
        setCertificate(certResponse.data);
        setQrData(qrResponse.data);
      } catch (error) {
        console.error('Error fetching certificate:', error);
        if (error.response?.status !== 404) {
          toast.error('Failed to load certificate');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/certificates/my/pdf`, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Origin': window.location.origin
        }
      });
      
      // Convert base64 to blob
      const byteCharacters = atob(response.data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = () => {
    if (qrData?.verification_url) {
      navigator.clipboard.writeText(qrData.verification_url);
      toast.success('Verification link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#F5A623] animate-spin" />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-4">
          No Certificate Found
        </h2>
        <p className="text-slate-600">
          Your certificate will be available once your membership is activated.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#0E285B] mb-6 sm:mb-8">
        My Certificate
      </h1>

      {/* Fullscreen Modal */}
      {showFullscreen && (
        <CertificateModal 
          certificate={certificate} 
          qrData={qrData} 
          onClose={() => setShowFullscreen(false)} 
        />
      )}

      {/* Mobile View - Simplified Card */}
      <div className="lg:hidden mb-6">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {/* Certificate Preview Thumbnail */}
          <div 
            className="relative bg-[#0A1628] p-6 cursor-pointer"
            onClick={() => setShowFullscreen(true)}
            data-testid="certificate-mobile-preview"
          >
            <div className="flex items-center justify-center gap-4">
              <Award className="h-12 w-12 text-[#C9A227]" />
              <div className="text-left">
                <h3 className="text-white font-semibold text-lg">{certificate.member_name}</h3>
                <p className="text-[#C9A227] text-sm uppercase">{certificate.membership_type} Member</p>
              </div>
            </div>
            <div className="absolute top-3 right-3 bg-white/10 rounded-full p-2">
              <Maximize2 className="h-5 w-5 text-white" />
            </div>
            <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-bold ${
              certificate.status === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              {certificate.status?.toUpperCase()}
            </div>
          </div>

          {/* Mobile Info */}
          <div className="p-4 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Certificate No:</span>
              <span className="font-mono text-[#0E285B]">{certificate.certificate_number}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Issue Date:</span>
              <span className="text-[#0E285B]">{certificate.issue_date}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Country:</span>
              <span className="text-[#0E285B]">{certificate.country || 'N/A'}</span>
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="p-4 pt-0 grid grid-cols-2 gap-3">
            <Button 
              onClick={() => setShowFullscreen(true)}
              variant="outline"
              className="border-[#0E285B] text-[#0E285B]"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              View Full
            </Button>
            <Button 
              onClick={handleDownload}
              disabled={downloading}
              className="bg-[#0E285B] text-white"
            >
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Download
            </Button>
          </div>

          {/* QR Code Section - Mobile */}
          <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
            <div className="flex items-center gap-3">
              {qrData?.verification_url && (
                <div className="bg-white p-2 rounded border">
                  <QRCodeSVG value={qrData.verification_url} size={60} level="M" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-[#0E285B]">Verification QR</p>
                <p className="text-xs text-slate-500">Scan to verify</p>
              </div>
            </div>
            <Button 
              onClick={handleShare}
              variant="ghost"
              size="sm"
              className="text-[#F5A623]"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:grid lg:grid-cols-4 gap-8">
        {/* Certificate Preview - Using Template Background */}
        <div className="lg:col-span-3">
          {/* Certificate Card with Template Background */}
          <div 
            className="relative overflow-hidden rounded-lg shadow-2xl bg-[#0A1628] cursor-pointer"
            style={{ aspectRatio: '1.414/1' }}
            onClick={() => setShowFullscreen(true)}
            data-testid="certificate-preview"
          >
            {/* Template Background Image */}
            <img 
              src="/certificate-bg.png" 
              alt="Certificate Background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Dynamic Content Overlay - Member name, statement and details */}
            <div className="absolute inset-0 flex flex-col items-center text-center px-12 pt-[26%]">
              {/* Member Name */}
              <h3 className="text-[#C9A227] text-xl md:text-2xl font-bold tracking-wide mb-1">
                {certificate.member_name}
              </h3>
              
              {/* Membership Statement */}
              <p className="text-white text-[10px] md:text-xs max-w-md leading-relaxed">
                has been admitted as a <span className="text-[#C9A227] font-semibold uppercase">{certificate.membership_type}</span> MEMBER of UIEHEC,
                in recognition of their commitment to international cooperation,
                academic excellence, and the advancement of higher education.
              </p>
              
              {/* Validity - smaller and closer */}
              <p className="text-white/70 text-[9px] md:text-[10px] mt-2 max-w-lg">
                Membership is effective from {certificate.valid_from || certificate.issue_date} and remains valid until {certificate.valid_until || 'Indefinite'},
                subject to the UIEHEC Charter and internal regulations.
              </p>
              
              {/* Certificate Details Box - positioned higher to avoid seal */}
              <div className="border border-[#C9A227]/50 rounded flex divide-x divide-[#C9A227]/30 text-xs mt-4 max-w-lg w-full">
                <div className="flex-1 p-2 text-center">
                  <p className="text-[#C9A227] font-semibold text-[10px]">Certificate No:</p>
                  <p className="text-white font-mono text-xs">{certificate.certificate_number}</p>
                </div>
                <div className="flex-1 p-2 text-center">
                  <p className="text-[#C9A227] font-semibold text-[10px]">Membership Type:</p>
                  <p className="text-white uppercase text-xs">{certificate.membership_type}</p>
                </div>
                <div className="flex-1 p-2 text-center">
                  <p className="text-[#C9A227] font-semibold text-[10px]">Issue Date:</p>
                  <p className="text-white text-xs">{certificate.issue_date}</p>
                </div>
              </div>
            </div>

            {/* QR Code - Bottom Right corner in empty space */}
            {qrData?.verification_url && (
              <div className="absolute bottom-[8%] right-[5%] bg-white p-1 rounded shadow-lg">
                <QRCodeSVG 
                  value={qrData.verification_url}
                  size={40}
                  level="M"
                />
              </div>
            )}

            {/* Status Badge */}
            <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${
              certificate.status === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              {certificate.status?.toUpperCase()}
            </div>

            {/* Fullscreen hint */}
            <div className="absolute bottom-4 left-4 bg-black/50 rounded-full px-3 py-1 text-white text-xs flex items-center gap-1">
              <Maximize2 className="h-3 w-3" />
              Click to enlarge
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-6">
            <Button 
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-12"
              data-testid="download-certificate-btn"
            >
              {downloading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Download className="mr-2 h-5 w-5" />
              )}
              Download PDF Certificate
            </Button>
            <Button 
              onClick={handleShare}
              variant="outline"
              className="flex-1 border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10 h-12"
              data-testid="share-certificate-btn"
            >
              <Share2 className="mr-2 h-5 w-5" />
              Copy Verification Link
            </Button>
          </div>
        </div>

        {/* Sidebar - QR Code & Info */}
        <div className="space-y-6">
          {/* QR Code Card */}
          <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="qr-code-section">
            <h3 className="font-semibold text-[#0E285B] mb-4 text-center">
              Verification QR Code
            </h3>
            {qrData?.verification_url && (
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-lg shadow-inner border">
                  <QRCodeSVG 
                    value={qrData.verification_url}
                    size={150}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500 text-center mt-4">
              Scan this QR code to verify the authenticity of this certificate
            </p>
          </div>

          {/* Quick Info Card */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="font-semibold text-[#0E285B] mb-3 text-sm">Certificate Details</h4>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-slate-500 text-xs">Certificate Number</dt>
                <dd className="font-mono text-[#0E285B]">{certificate.certificate_number}</dd>
              </div>
              <div>
                <dt className="text-slate-500 text-xs">Issue Date</dt>
                <dd className="text-[#0E285B]">{certificate.issue_date}</dd>
              </div>
              <div>
                <dt className="text-slate-500 text-xs">Member Since</dt>
                <dd className="text-[#0E285B]">{certificate.valid_from || certificate.issue_date}</dd>
              </div>
              <div>
                <dt className="text-slate-500 text-xs">Country</dt>
                <dd className="text-[#0E285B]">{certificate.country || 'N/A'}</dd>
              </div>
            </dl>
          </div>

          {/* View Online Button */}
          <a 
            href={qrData?.verification_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Button 
              variant="outline" 
              className="w-full border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              <Eye className="mr-2 h-4 w-4" />
              View Public Page
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
